import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  SelectChangeEvent,
  SxProps,
  Theme,
} from "@mui/material";
import { FieldCellValueUI, FormFieldValueGroupUI, parseSafeInt } from "Backend";
import PSelect from "components/PSelect";
import { Cell, FieldValueGroupSelectionStatus } from "generated/graphql";
import { P } from "theme/typography";
import displayCellValue from "./displayCellValue";
import { useContext } from "react";
import { FormFieldContext, GroupedValueGroup } from "contexts/FormFieldContext";
import { Star } from "@mui/icons-material";
import { goldStar } from "theme/palette";
import SourceTypeIcon from "./SourceTypeIcon";
import theme from "theme";
import { distinctReferencesInGroup } from "./form-field-helpers";
import { sortValueGroups } from "./sortValueGroups";
import { useFilteredValueGroups } from "helpers/hooks/useFilteredValueGroups";

function responseMenuItem({
  group,
  vgDate,
  vgLensSubtype,
  vgName,
  vgRecordType,
  formCells,
  groupName,
}: {
  group: FormFieldValueGroupUI;
  vgDate?: FieldCellValueUI;
  vgLensSubtype?: FieldCellValueUI;
  vgName?: FieldCellValueUI;
  vgRecordType?: FieldCellValueUI;
  groupName?: string;
  formCells: Cell[];
}) {
  const distinctReferences = distinctReferencesInGroup(group);
  return (
    <MenuItem
      key={group.id}
      value={groupName !== undefined ? `${group.id}__${groupName}` : group.id}
      sx={{ display: "grid", gridTemplateColumns: "20px 1fr", p: 1, minWidth: 225, pr: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        {group.selectionStatus === FieldValueGroupSelectionStatus.Selected && (
          <Star sx={{ color: goldStar, height: 14, width: 14 }} />
        )}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%" }}>
        <P>Entry {group.responseNumber}</P>
        {group.source && <SourceTypeIcon valueGroupSource={group.source} sx={{ height: 14, width: 14 }} />}
        <Box sx={{ flexGrow: 1 }} />
        {distinctReferences !== undefined && <P>{distinctReferences} Ref</P>}
      </Box>
      <div />
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        {vgDate?.value.dateValue && (
          <P variant="xxs" sx={{ color: "grey.400" }}>
            {(formCells.find((c) => c.id === vgDate.cellId)?.name ?? "") + ":"} {displayCellValue(vgDate?.value)}
          </P>
        )}
        {vgLensSubtype?.value && (
          <P variant="xxs" sx={{ color: "grey.400" }}>
            {(formCells.find((c) => c.id === vgLensSubtype.cellId)?.name ?? "") + ":"}{" "}
            {displayCellValue(vgLensSubtype.value)}
          </P>
        )}
        {vgName?.value.textValue && (
          <P variant="xxs" sx={{ color: "grey.400" }}>
            {(formCells.find((c) => c.id === vgName.cellId)?.name ?? "") + ":"} {displayCellValue(vgName?.value)}
          </P>
        )}
        {vgRecordType?.value.textValue && (
          <P variant="xxs" sx={{ color: "grey.400" }}>
            {(formCells.find((c) => c.id === vgRecordType.cellId)?.name ?? "") + ":"}{" "}
            {displayCellValue(vgRecordType?.value)}
          </P>
        )}
      </Box>
    </MenuItem>
  );
}

const ValueGroupSelect = (props: {
  dateCellIndex: number;
  lensSubtypeCellIndex: number;
  nameCellIndex: number;
  recordTypeCellIndex: number;
  label: string;
  groupId: number;
  groupResponseNumber: number;
  setGroupId: (groupId: number) => void;
  valueGroups: FormFieldValueGroupUI[];
  formCells: Cell[];
  formControlSX?: SxProps<Theme>;
  addNewValueGroup?: boolean;
  dataPendo?: string;
  className?: string;
}) => {
  const {
    dateCellIndex,
    lensSubtypeCellIndex,
    nameCellIndex,
    recordTypeCellIndex,
    label,
    groupId,
    groupResponseNumber,
    setGroupId,
    valueGroups,
    formCells,
    formControlSX,
    addNewValueGroup,
    dataPendo,
    className,
  } = props;
  return (
    <FormControl data-pendo={dataPendo} sx={formControlSX} className={className}>
      <InputLabel>{label}</InputLabel>
      <PSelect
        label={label}
        value={groupId}
        renderValue={() => {
          return !addNewValueGroup || groupResponseNumber !== -1 ? (
            <>Entry {groupResponseNumber}</>
          ) : (
            <>Add New Entry</>
          );
        }}
        onChange={(evt) => setGroupId(evt.target.value as number)}>
        {addNewValueGroup && <MenuItem value={-1}>Add New Entry</MenuItem>}
        {valueGroups.map((existingVG) => {
          const vgDate = dateCellIndex !== -1 ? existingVG.values[dateCellIndex] : undefined;
          const vgLensSubtype = lensSubtypeCellIndex !== -1 ? existingVG.values[lensSubtypeCellIndex] : undefined;
          const vgName = nameCellIndex !== -1 ? existingVG.values[nameCellIndex] : undefined;
          const vgRecordType = recordTypeCellIndex !== -1 ? existingVG.values[recordTypeCellIndex] : undefined;
          return responseMenuItem({
            group: existingVG,
            vgDate,
            vgLensSubtype,
            vgName,
            vgRecordType,
            formCells,
          });
        })}
      </PSelect>
    </FormControl>
  );
};

export default ValueGroupSelect;

function parseOnChangeEvt(evt: SelectChangeEvent<unknown>): {
  groupId?: number;
  groupName?: string;
} {
  const value = evt.target.value as string;
  const [groupId, groupName] = value.split("__");
  return {
    groupId: parseSafeInt(groupId),
    groupName,
  };
}

// Assumes to be inside of a FormFieldContext
export function ValueGroupSelectGrouped({
  label,
  groupId,
  setGroupId,
  formControlSX,
  addNewValueGroup,
  dataPendo,
}: {
  label: string;
  groupId: number;
  setGroupId: (value: { groupId?: number; groupName?: string }) => void;
  formControlSX?: SxProps<Theme>;
  addNewValueGroup?: boolean;
  dataPendo?: string;
}) {
  const ffContext = useContext(FormFieldContext);
  const { groupedAndFilteredValueGroups: groupedValueGroup } = useFilteredValueGroups();
  const foundGroupedValueGroup = groupedValueGroup.find((group) => group.valueGroups.find((vg) => vg.id === groupId));
  const responseNumber = foundGroupedValueGroup?.valueGroups.find((vg) => vg.id === groupId)?.responseNumber ?? "";
  const groupLabel = foundGroupedValueGroup
    ? foundGroupedValueGroup.cellValue || `No ${ffContext.groupByCell?.name ?? "Data"}`
    : label;

  const menuItems = ({ groups }: { groups: GroupedValueGroup[] }) => {
    return groups
      .map((group, index) => {
        const groupSort = ffContext.fieldUIState?.groupSorts[group.cellValue];
        const menuHeader = group.cellValue || `No ${ffContext.groupByCell?.name ?? "Data"}`;
        const sortedValueGroups =
          groupSort !== undefined
            ? (sortValueGroups(group.valueGroups, groupSort.sortDir, groupSort.cell) as FormFieldValueGroupUI[])
            : group.valueGroups;
        return [
          <ListSubheader
            sx={{
              fontSize: 14,
              fontWeight: 700,
              color: theme.palette.grey[900],
              borderTop: index !== 0 ? `1px solid ${theme.palette.grey[100]}` : undefined,
            }}
            key={group.cellValue}>
            {menuHeader}
            {groupSort !== undefined ? ` (sorted by ${groupSort.cell.name})` : ""}
          </ListSubheader>,
          ...sortedValueGroups.map((valueGroup) => {
            const vgDate = ffContext.dateCellIndex !== -1 ? valueGroup.values[ffContext.dateCellIndex] : undefined;
            const vgLensSubtype =
              ffContext.lensSubtypeCellIndex !== -1 ? valueGroup.values[ffContext.lensSubtypeCellIndex] : undefined;
            const vgName = ffContext.nameCellIndex !== -1 ? valueGroup.values[ffContext.nameCellIndex] : undefined;
            const vgRecordType =
              ffContext.recordTypeCellIndex !== -1 ? valueGroup.values[ffContext.recordTypeCellIndex] : undefined;
            return responseMenuItem({
              group: valueGroup,
              vgDate,
              vgLensSubtype,
              vgName,
              vgRecordType,
              formCells: ffContext.formField.cells,
              groupName: group.cellValue,
            });
          }),
        ];
      })
      .flat();
  };

  return (
    <FormControl data-pendo={dataPendo} sx={formControlSX} className="w-48">
      <InputLabel>{groupLabel}</InputLabel>
      <PSelect
        label={groupLabel}
        value={`${groupId}__${foundGroupedValueGroup?.cellValue}`}
        renderValue={() => (!addNewValueGroup ? <>Entry {responseNumber}</> : <>Add New Entry</>)}
        onChange={(evt) => setGroupId(parseOnChangeEvt(evt))}>
        {addNewValueGroup && <MenuItem value={-1}>Add New Entry</MenuItem>}
        {menuItems({ groups: groupedValueGroup })}
      </PSelect>
    </FormControl>
  );
}
